import { CommonModule } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { LocaleService } from '@next/core-lib/i18n';

import {
  BreederWeekRegistrationErrorChecker,
  BreederWeekRegistrationErrorStateMatcher,
} from './breeder-week-registration-error-state-matcher';
import { SharedModule } from '../../../../shared/shared.module';
import { BreederValueRegistration } from '../../../models/flock.model';

export type BreederWeekRegistrationField = Extract<
  keyof BreederValueRegistration,
  | 'henMortality'
  | 'roosterMortality'
  | 'henWeight'
  | 'roosterWeight'
  | 'henFeedIntakeInGrams'
  | 'roosterFeedIntakeInGrams'
  | 'henWaterIntakeInLiters'
  | 'roosterWaterIntakeInLiters'
  | 'eggCount'
  | 'eggWeightInGrams'
  | 'fertilityPercentage'
  | 'hatchPercentage'
  | 'lightHours'
  | 'lightIntensity'
>;

interface BreederFieldConfig {
  name: BreederWeekRegistrationField;
  labelKey: string;
  step: number;
  suffix?: string;
}

const FIELD_CONFIGS: BreederFieldConfig[] = [
  { name: 'henMortality', labelKey: 'week-registration.henMortality', step: 1 },
  { name: 'roosterMortality', labelKey: 'week-registration.roosterMortality', step: 1 },
  { name: 'henWeight', labelKey: 'week-registration.henWeight', step: 0.01, suffix: 'g' },
  { name: 'roosterWeight', labelKey: 'week-registration.roosterWeight', step: 0.01, suffix: 'g' },
  {
    name: 'henFeedIntakeInGrams',
    labelKey: 'week-registration.henFeedIntakeInGrams',
    step: 0.01,
    suffix: 'g',
  },
  {
    name: 'roosterFeedIntakeInGrams',
    labelKey: 'week-registration.roosterFeedIntakeInGrams',
    step: 0.01,
    suffix: 'g',
  },
  {
    name: 'henWaterIntakeInLiters',
    labelKey: 'week-registration.henWaterIntakeInLiters',
    step: 0.01,
    suffix: 'l',
  },
  {
    name: 'roosterWaterIntakeInLiters',
    labelKey: 'week-registration.roosterWaterIntakeInLiters',
    step: 0.01,
    suffix: 'l',
  },
  { name: 'eggCount', labelKey: 'week-registration.eggCount', step: 1 },
  {
    name: 'eggWeightInGrams',
    labelKey: 'week-registration.eggWeightInGrams',
    step: 0.01,
    suffix: 'g',
  },
  {
    name: 'fertilityPercentage',
    labelKey: 'week-registration.fertilityPercentage',
    step: 0.01,
    suffix: '%',
  },
  {
    name: 'hatchPercentage',
    labelKey: 'week-registration.hatchPercentage',
    step: 0.01,
    suffix: '%',
  },
  { name: 'lightHours', labelKey: 'week-registration.lightHours', step: 0.01, suffix: 'h' },
  {
    name: 'lightIntensity',
    labelKey: 'week-registration.lightIntensity',
    step: 0.01,
    suffix: 'lux',
  },
];

@Component({
  selector: 'app-breeder-week-registration-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, SharedModule],
  templateUrl: './breeder-week-registration-form.component.html',
  styleUrls: ['./breeder-week-registration-form.component.scss'],
})
export class BreederWeekRegistrationFormComponent implements BreederWeekRegistrationErrorChecker {
  private readonly errorStateMatchers = new Map<
    BreederWeekRegistrationField,
    BreederWeekRegistrationErrorStateMatcher
  >();

  readonly formGroup = input.required<FormGroup>();
  readonly showFields = input.required<BreederWeekRegistrationField[]>();
  readonly index = input.required<number>();
  readonly mortalityChanged = output<{
    index: number;
    henMortality: number;
    roosterMortality: number;
  }>();

  readonly visibleFields = computed(() =>
    FIELD_CONFIGS.filter((field) => this.showFields().includes(field.name)),
  );

  constructor(private readonly locale: LocaleService) {}

  onMortalityChange(): void {
    this.mortalityChanged.emit({
      index: this.index(),
      henMortality: this.formGroup().controls.henMortality.value as number,
      roosterMortality: this.formGroup().controls.roosterMortality.value as number,
    });
  }

  isMortalityField(field: BreederWeekRegistrationField): boolean {
    return field === 'henMortality' || field === 'roosterMortality';
  }

  getErrorStateMatcher(field: BreederWeekRegistrationField): ErrorStateMatcher {
    let matcher = this.errorStateMatchers.get(field);
    if (!matcher) {
      matcher = new BreederWeekRegistrationErrorStateMatcher(this, field);
      this.errorStateMatchers.set(field, matcher);
    }

    return matcher;
  }

  hasError(field: BreederWeekRegistrationField): boolean {
    return !!this.formGroup().controls[field]?.errors || this.hasFormLevelError(field);
  }

  private hasFormLevelError(field: BreederWeekRegistrationField): boolean {
    if (field !== 'hatchPercentage') {
      return false;
    }

    const fertilityPercentage = this.formGroup().get('fertilityPercentage')?.value;
    const hatchPercentage = this.formGroup().get('hatchPercentage')?.value;
    return (
      fertilityPercentage != null &&
      hatchPercentage != null &&
      fertilityPercentage < hatchPercentage
    );
  }

  getValidationErrorMessage(field: BreederWeekRegistrationField): string {
    const errors = this.formGroup().controls[field]?.errors;

    if (errors?.pattern && this.isMortalityField(field)) {
      return this.locale.translate('week-registration.whole-number-required');
    }

    if (errors?.min || errors?.max) {
      const min = errors.min?.min ?? 0;
      const max = errors.max?.max;
      return max === undefined
        ? `${this.locale.translate('week-registration.out-of-range')} ${min}+`
        : `${this.locale.translate('week-registration.out-of-range')} ${min} - ${max}`;
    }

    if (field === 'hatchPercentage' && this.hasFormLevelError(field)) {
      return this.locale.translate('week-registration.hatch-greater-than-fertility');
    }

    return this.locale.translate('week-registration.out-of-range');
  }
}
